import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';

import { type RecommendedApp } from '../index';

/**
 * Statsig experiment that selects which "missing app" activation banner variant
 * (if any) to show in the side-nav. Sourced from the
 * `twcg_missing_app_activation_banner` audience.
 */
export const MISSING_APP_EXPERIMENT = 'twcg_missing_app_activation_banner';

export type ExperimentArm = 'control' | 'generic' | 'personalized';

/**
 * Reads the missing-app experiment arm for the given tenant. Anything other
 * than `generic` / `personalized` collapses to `control` so callers can rely on
 * an exhaustive union.
 */
export const getMissingAppExperimentArm = (
	statsig: RequestContext['statsig'],
	tenantId: string,
): ExperimentArm => {
	const arm = statsig.getExperimentValue(
		{ identifiers: { tenantId } },
		{ name: MISSING_APP_EXPERIMENT, parameter: 'arm', defaultValue: 'control' },
	);
	return arm === 'generic' || arm === 'personalized' ? arm : 'control';
};

/**
 * The presence of `recommendedApp` on the message context is the contract for
 * the missing-app activation flow — it's only populated by the
 * `twcg_missing_app_activation_banner` audience. Use this helper instead of
 * truthiness checks so the intent is clear at every call site.
 */
export const isMissingAppFlow = (context: {
	recommendedApp?: RecommendedApp;
}): context is { recommendedApp: RecommendedApp } => Boolean(context.recommendedApp);
